import { CountryCode } from 'libphonenumber-js'
import { useCallback, useState } from 'react'

import { useAuth } from '../context/auth'
import { removeReferralSlug } from '../lib/referral'
import { StandardAccountFormValues } from '../screens/CreateAccountProfile/CreateAccountProfile'
import { B2BUserRegistrationData, doesPhoneAlreadyExists, getCreateAccountProfilePayload } from '../util/auth'
import { mixPanelIdentify, mixPanelTrackEvent } from '../util/mixpanel'

import { useTracking } from './tracking/use-tracking'
import { useCreateProfileValidation } from './use-create-profile-validation'

export enum AccountType {
  SOCIAL = 'SOCIAL',
  PHONE = 'PHONE',
}

interface UseCreateProfileParams {
  countryCode: CountryCode
  onRegistration: (input: StandardAccountFormValues, token?: string) => Promise<void>
  redirectTo: VoidFunction
  accountType: AccountType
}

export function useCreateStandardProfile({
  accountType,
  countryCode,
  onRegistration,
  redirectTo,
}: UseCreateProfileParams) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { registerPatientAndLogin, validatePatientProfileData } = useAuth()
  const { trackSignUp } = useTracking()
  const { cpfExists, emailExists, handleError, phoneNumberExists, setCpfExists, setEmailExists, setPhoneNumberExists } =
    useCreateProfileValidation()

  const checkIfPhoneAlreadyExists = useCallback(
    async (phone: string) => {
      setPhoneNumberExists(false)
      const numberExists = await doesPhoneAlreadyExists(phone, countryCode)
      setPhoneNumberExists(numberExists)
    },
    [countryCode, setPhoneNumberExists]
  )

  const validatePatientProfile = useCallback(
    async (input: StandardAccountFormValues, token?: string) => {
      try {
        if (!token) {
          return
        }

        const profile = getCreateAccountProfilePayload(input)
        await validatePatientProfileData(profile, token, accountType)
      } catch (e: unknown) {
        handleError(e)
      }
    },
    [accountType, handleError, validatePatientProfileData]
  )

  const createPatientProfile = useCallback(
    async (input: StandardAccountFormValues, token?: string, b2bData?: B2BUserRegistrationData) => {
      try {
        if (!token) {
          return
        }

        const profile = getCreateAccountProfilePayload(input, b2bData)
        await registerPatientAndLogin(profile, token, accountType)

        await onRegistration(input)

        removeReferralSlug()
        trackSignUp()
        setIsSubmitting(false)

        await mixPanelIdentify(input.email)
        mixPanelTrackEvent('Sign up')

        redirectTo()
      } catch (e: unknown) {
        handleError(e)
      }
    },
    [accountType, handleError, onRegistration, redirectTo, registerPatientAndLogin, trackSignUp]
  )

  return {
    checkIfPhoneAlreadyExists,
    createPatientProfile,
    validatePatientProfile,
    isSubmitting,
    phoneNumberExists,
    emailExists,
    setEmailExists,
    cpfExists,
    setCpfExists,
    setIsSubmitting,
    setPhoneNumberExists,
  }
}
